import { type SheetStation, coverGroupOf, otherSide } from "../../lib/weekSheet/stationGroups";
/**
 * #2945 slice 4 — **Team today**, pure. No React, no I/O. Unit-tested in
 * `web/tests/lib/teamTodayModel.test.ts`.
 *
 * The member's opening screen (founder Q1 (a)) reads the same week sheet the
 * Week sheet screen draws (`lib/weekSheet/weekSheetModel.ts`, slice 1) and
 * cuts it to what a member asks first:
 *
 *   1. The date line: "You are on ward 55, AM and PM." (or "You are off
 *      today", on call tonight, post call today).
 *   2. My ward: the team's levels down, the sessions across, full names,
 *      my name marked. Under it (#3022 S8), "Consultants on ward 55": every
 *      consultant the department's sheet names on my ward today, with the
 *      beds, in bed number order. They are people, not seats.
 *   3. The other side: the other wards of my cover group, contact links
 *      first (founder Q2 (a), `otherSide` in `stationGroups.ts`), one
 *      session at a time. An empty level reads "No REG today".
 *   4. On call tonight, one group per level with each call post and its
 *      holder, and the post-call line under it, by level.
 *   5. This week: seven days with my own ward, call post, Post call or Off.
 *
 * Levels come from the sheet, so from the team's own tiers in their order,
 * never hardcoded. Seats come from the member read (released seats only), so
 * a member never sees a draft, a leave row or anyone's blocks.
 *
 * With `member_full_roster` off the screen has the member's own rows only:
 * `myDaysFromOwnRows` reads the date line and the week strip from them.
 */
import {
  NO_LEVEL_KEY,
  OTHER_LEVEL_KEY,
  type SheetConsultant,
  type SheetLevel,
  type SheetName,
  type SheetRow,
  type SheetSession,
  type WeekSheet,
  addDays,
  cellNames,
  consultantsOnWard,
  placeInSentence,
  wardConsultantsOn,
} from "../../lib/weekSheet/weekSheetModel";
import { type CalendarAssignment, roleDisplay } from "../calendar/calendarModel";

export const OFF_TODAY_LINE = "You are off today.";
export const POST_CALL_TODAY_LINE = "You are post call today.";
export const NOBODY_ON_CALL = "No one on call tonight.";
export const POST_CALL_PREFIX = "Post call today";
export const OFF_LABEL = "Off";
export const POST_CALL_LABEL = "Post call";

// ─── Places ──────────────────────────────────────────────────────────

/** A ward number heads as "Ward 55"; a named station as itself. */
export function placeTitle(name: string): string {
  // #3078 — a name that already holds the word ("32 Ward") gets no second one.
  return /^\d/.test(name) && !/\bward\b/i.test(name) ? `Ward ${name}` : name;
}

/** "AM", "AM and PM", "AM, PM and Night". */
export function joinAnd(parts: readonly string[]): string {
  if (parts.length <= 1) return parts[0] ?? "";
  return `${parts.slice(0, -1).join(", ")} and ${parts[parts.length - 1]}`;
}

// ─── My day ──────────────────────────────────────────────────────────

export interface MyWardSeat {
  ward: string;
  /** Session labels in sheet order ("AM", "PM"). */
  sessions: string[];
}

export interface MyDay {
  date: string;
  /** My ward seats on the day, in sheet order. */
  wards: MyWardSeat[];
  /** My call posts on the day ("R2"); "" for a call with no post name. */
  callPosts: string[];
  /** On call the night before and no ward seat this morning (stand-in). */
  postCall: boolean;
  /** No ward seat, no call, not post call. */
  off: boolean;
}

function rowsOf(sheet: WeekSheet): SheetRow[] {
  return [
    ...sheet.groups.flatMap((g) => g.rows),
    ...sheet.blockRows.filter((r) => r.kind === "clinic"),
  ];
}

function ownInCell(row: SheetRow, date: string, session: string, levels: SheetLevel[]): boolean {
  return levels.some((l) => cellNames(row, date, session, l.key).some((n) => n.own));
}

function ownAnywhere(row: SheetRow | undefined, sheet: WeekSheet, date: string): SheetName[] {
  if (!row) return [];
  const out: SheetName[] = [];
  for (const s of sheet.sessions)
    for (const l of sheet.levels)
      for (const n of cellNames(row, date, s.key, l.key)) if (n.own) out.push(n);
  return out;
}

/** My day from the week sheet's own marks. */
export function myDayFromSheet(sheet: WeekSheet, date: string): MyDay {
  const wards: MyWardSeat[] = [];
  for (const row of rowsOf(sheet)) {
    const sessions = sheet.sessions
      .filter((s) => ownInCell(row, date, s.key, sheet.levels))
      .map((s) => s.label);
    if (sessions.length > 0) wards.push({ ward: row.label, sessions });
  }
  const onCallRow = sheet.blockRows.find((r) => r.kind === "on_call");
  const postRow = sheet.blockRows.find((r) => r.kind === "post_call");
  const callPosts = [...new Set(ownAnywhere(onCallRow, sheet, date).map((n) => n.post ?? ""))];
  const postCall = ownAnywhere(postRow, sheet, date).length > 0;
  return {
    date,
    wards,
    callPosts,
    postCall,
    off: wards.length === 0 && callPosts.length === 0 && !postCall,
  };
}

/** "R2" from the own row's role (`ONCALL_R2` → "On-call R2"); "" when bare. */
function ownCallPost(row: CalendarAssignment): string {
  const station = roleDisplay(row.role).station;
  return station.startsWith("On-call") ? station.slice("On-call".length).trim() : "";
}

function ownIsCall(row: CalendarAssignment): boolean {
  return row.kind === "oncall" || Boolean(row.is_oncall) || roleDisplay(row.role).isOnCall;
}

/**
 * My days from my own rows (`member_full_roster` off). The POST CALL
 * stand-in is the same as the sheet's: a call the day before and no ward
 * seat that morning.
 */
export function myDaysFromOwnRows(
  rows: readonly CalendarAssignment[],
  dates: readonly string[],
): MyDay[] {
  return dates.map((date) => {
    const wards: MyWardSeat[] = [];
    const callPosts: string[] = [];
    for (const row of rows) {
      if (row.date !== date) continue;
      if (ownIsCall(row)) {
        const post = ownCallPost(row);
        if (!callPosts.includes(post)) callPosts.push(post);
        continue;
      }
      const display = roleDisplay(row.role);
      const session = display.session ?? row.session ?? "";
      const seat = wards.find((w) => w.ward === display.station);
      if (seat) {
        if (session && !seat.sessions.includes(session)) seat.sessions.push(session);
      } else wards.push({ ward: display.station, sessions: session ? [session] : [] });
    }
    for (const w of wards) w.sessions.sort((a, b) => sessionRank(a) - sessionRank(b));
    const before = addDays(date, -1);
    const calledBefore = rows.some((r) => r.date === before && ownIsCall(r));
    const postCall = calledBefore && wards.length === 0;
    return {
      date,
      wards,
      callPosts,
      postCall,
      off: wards.length === 0 && callPosts.length === 0 && !postCall,
    };
  });
}

function sessionRank(label: string): number {
  const u = label.toUpperCase();
  return u === "AM" ? 0 : u === "PM" ? 1 : 2;
}

/**
 * The line under the date. One ward: "You are on ward 55, AM and PM."
 * Several: "You are on ward 55 (AM) and ward 31 (PM)." A call adds "You are
 * on call tonight, R2." No seat: "You are off today."
 */
export function dateLine(day: MyDay): string {
  const parts: string[] = [];
  if (day.wards.length === 1) {
    const w = day.wards[0] as MyWardSeat;
    const when = joinAnd(w.sessions);
    parts.push(`You are on ${placeInSentence(w.ward)}${when ? `, ${when}` : ""}.`);
  } else if (day.wards.length > 1) {
    const each = day.wards.map(
      (w) => `${placeInSentence(w.ward)}${w.sessions.length ? ` (${joinAnd(w.sessions)})` : ""}`,
    );
    parts.push(`You are on ${joinAnd(each)}.`);
  }
  if (day.callPosts.length > 0) {
    const posts = day.callPosts.filter(Boolean);
    parts.push(`You are on call tonight${posts.length ? `, ${joinAnd(posts)}` : ""}.`);
  }
  if (parts.length === 0) return day.postCall ? POST_CALL_TODAY_LINE : OFF_TODAY_LINE;
  return parts.join(" ");
}

// ─── My ward ─────────────────────────────────────────────────────────

export interface LevelRow {
  level: SheetLevel;
  /** One list of names per column (a session, or a ward). */
  cells: SheetName[][];
}

export interface WardTable {
  ward: string;
  title: string;
  sessions: SheetSession[];
  rows: LevelRow[];
  /** #3022 S8 — "Consultants on ward 55". */
  consultantsTitle: string;
  /** #3022 S8 — the ward's consultants today, in bed number order; [] when
   *  the sheet names nobody. Not seats. */
  consultants: SheetConsultant[];
}

function wardRow(sheet: WeekSheet, ward: string): SheetRow | undefined {
  const fold = ward.toLocaleLowerCase();
  return rowsOf(sheet).find((r) => r.label.toLocaleLowerCase() === fold);
}

/** My ward on a day: levels down, sessions across. */
export function wardTable(sheet: WeekSheet, ward: string, date: string): WardTable | null {
  const row = wardRow(sheet, ward);
  if (!row) return null;
  return {
    ward: row.label,
    title: placeTitle(row.label),
    sessions: sheet.sessions,
    rows: sheet.levels.map((level) => ({
      level,
      cells: sheet.sessions.map((s) => cellNames(row, date, s.key, level.key)),
    })),
    consultantsTitle: consultantsOnWard(row.label),
    consultants: wardConsultantsOn(sheet, row.label, date),
  };
}

// ─── The other side ──────────────────────────────────────────────────

export interface OtherSideTable {
  /** "group A", a longer group's own text, or null (contact links only). */
  groupLabel: string | null;
  session: SheetSession;
  /** The other wards, contact links first, then the group's order. */
  wards: string[];
  rows: LevelRow[];
}

/** "group A" for a one-letter group; a longer group's text as written. */
export function groupLabel(group: string | null): string | null {
  if (!group) return null;
  return /^\p{L}$/u.test(group) ? `group ${group}` : group;
}

/** The other side of my ward for one session (founder Q2 (a)). */
export function otherSideTable(
  sheet: WeekSheet,
  stations: readonly SheetStation[],
  ward: string,
  date: string,
  sessionKey: string,
): OtherSideTable | null {
  const session = sheet.sessions.find((s) => s.key === sessionKey) ?? sheet.sessions[0];
  if (!session) return null;
  const others = otherSide(stations, ward)
    .map((name) => wardRow(sheet, name))
    .filter((r): r is SheetRow => Boolean(r));
  if (others.length === 0) return null;
  return {
    groupLabel: groupLabel(coverGroupOf(stations, ward)),
    session,
    wards: others.map((r) => r.label),
    rows: sheet.levels.map((level) => ({
      level,
      cells: others.map((r) => cellNames(r, date, session.key, level.key)),
    })),
  };
}

/** "No REG today" for an empty level; "Nobody" for a team with no levels. */
export function emptyLevelLine(level: SheetLevel): string {
  return level.key === NO_LEVEL_KEY ? "Nobody" : `No ${level.short} today`;
}

// ─── On call and post call ───────────────────────────────────────────

export interface CallLine {
  level: SheetLevel;
  names: SheetName[];
}

function blockNames(sheet: WeekSheet, kind: "on_call" | "post_call", date: string, level: string) {
  const row = sheet.blockRows.find((r) => r.kind === kind);
  if (!row) return [];
  const out: SheetName[] = [];
  for (const s of sheet.sessions)
    for (const n of cellNames(row, date, s.key, level))
      if (!out.some((o) => o.memberId === n.memberId && o.post === n.post)) out.push(n);
  return out;
}

/**
 * On call tonight: one group per level that has a call, in level order. The
 * screen labels each group once and lists every call post with its holder.
 */
export function onCallTonight(sheet: WeekSheet, date: string): CallLine[] {
  return sheet.levels
    .map((level) => ({ level, names: blockNames(sheet, "on_call", date, level.key) }))
    .filter((l) => l.names.length > 0);
}

/** Post call today, every level, in level order. */
export function postCallToday(sheet: WeekSheet, date: string): SheetName[] {
  return postCallByLevel(sheet, date).flatMap((l) => l.names);
}

/**
 * Post call today grouped by level, in level order, a name once. The screen
 * reads "Post call today · REG a, b · MO c" (#2945 walkthrough defect 5).
 */
export function postCallByLevel(sheet: WeekSheet, date: string): CallLine[] {
  const seen = new Set<string>();
  const out: CallLine[] = [];
  for (const level of sheet.levels) {
    const names = blockNames(sheet, "post_call", date, level.key).filter((n) => {
      if (seen.has(n.memberId)) return false;
      seen.add(n.memberId);
      return true;
    });
    if (names.length > 0) out.push({ level, names });
  }
  return out;
}

/**
 * A level the team named (REG, MO, HO). The one "Names" column of a team
 * with no levels and the "Other" column of an unlevelled member are not:
 * their lines carry names only.
 */
export function levelKnown(level: SheetLevel): boolean {
  return level.key !== NO_LEVEL_KEY && level.key !== OTHER_LEVEL_KEY;
}

/** "Post call today · REG Daniel Sim, Koh Mei Ling · MO Irfan Hakim". */
export function postCallLine(lines: readonly CallLine[]): string {
  const parts = lines.map((l) => {
    const names = l.names.map((n) => n.fullName).join(", ");
    return levelKnown(l.level) ? `${l.level.short} ${names}` : names;
  });
  return [POST_CALL_PREFIX, ...parts].join(" · ");
}

// ─── This week ───────────────────────────────────────────────────────

export interface StripDay {
  date: string;
  /** My ward chips ("55"). */
  wards: string[];
  /** My call posts ("R2", "Night" for "Night call"; "Call" for a call with
   *  no post name). */
  posts: string[];
  postCall: boolean;
  off: boolean;
}

/**
 * #3078 — a call's chip on the week strip, 35 to 44 px wide inside: the post
 * name without a closing "call" or "on call" ("Night call" → "Night", "HD
 * Call" → "HD", "Duty supervisor on call" → "Duty supervisor"), since the
 * chip's amber already says call. "on call" goes as one unit so no "on" hangs
 * at the end. "Call" for a call with no name, or a name with nothing left
 * ("On call"). Before, the chip cut "Night call" to "Nig…". A word still too
 * wide for the chip ("supervisor") gets the short form below.
 */
const CLOSING_CALL = /(^|\s+)(on[\s-]*)?call$/i;

function callChipLabel(post: string): string {
  const short = post.trim().replace(CLOSING_CALL, "").trim();
  return short || "Call";
}

/**
 * #3078 — a week-strip chip's short form, for a name with a word too wide
 * for its chip (`TeamWeekStrip` measures; it never guesses). A name of more
 * words shows its initials, with numbers and two-letter codes whole ("Duty
 * supervisor" → "DS", "21 Triage" → "21T", "R2 night" → "R2N"). One word
 * shows its first three letters, as the Week sheet's level heads do
 * ("Registrar" → "Reg"). A chip never breaks inside a word.
 */
export function dayChipShortLabel(label: string): string {
  const words = label.trim().split(/\s+/).filter(Boolean);
  if (words.length === 1) {
    const word = words[0] as string;
    return word.length <= 4 ? word : word.slice(0, 3);
  }
  return words
    .map((w) => (/^\p{N}/u.test(w) || w.length <= 2 ? w : (w[0] ?? "").toUpperCase()))
    .join("");
}

export function stripDay(day: MyDay): StripDay {
  return {
    date: day.date,
    wards: day.wards.map((w) => w.ward),
    posts: day.callPosts.map(callChipLabel),
    postCall: day.postCall,
    off: day.off,
  };
}

/** The week strip from the sheet. */
export function weekStrip(sheet: WeekSheet, dates: readonly string[]): StripDay[] {
  return dates.map((d) => stripDay(myDayFromSheet(sheet, d)));
}

/** The Week sheet tab (#3063) on a day. Before #3063 a member read the sheet
 *  inside the Team tab (`/me/team?view=sheet&date=`); `TeamTabScreen` sends
 *  that old address here. */
export function teamSheetHref(date: string): string {
  return `/me/sheet?date=${date}`;
}
